import { useDebounceFn, useMemoizedFn, useMount } from 'ahooks';
import { Form, Radio } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import { UploadFile } from 'antd/lib/upload/interface';
import { SectionHeading, Text } from 'components/RevxTypography';
import { ArtworkOptionEnum, StepActionTypeEnum } from 'domains/skuDomain';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StepComponentProps } from '.';

import { useAppDispatch } from 'app/hooks';
import { FileItem } from 'domains/workspaceDetailDomain';
import SkuArtFile from '../components/SkuArtFile';

export const Step5UploadFiles = ({
  sku,
  topSku,
  skuPart,
}: StepComponentProps) => {
  const { t } = useTranslation();
  const [form] = useForm();
  const dispatch = useAppDispatch();
  const [showError, setShowError] = useState<boolean>(false);

  useMount(() => {
    form.setFieldsValue({
      proofNeeded: sku.proofNeeded,
      skipArtFile: sku.skipArtFile,
      artworkOption: sku.artworkOption,
    });

    dispatch.sku.registerStepAction({
      [StepActionTypeEnum.STEP5_UPLOADFILES]: submit,
    });
  });

  const artworksList = useMemo(() => {
    const returnData: UploadFile<any>[] = [];
    (sku?.artworks || []).forEach((item) => {
      returnData.push({
        url: item.url,
        name: item.fileName,
        fileName: item.fileName,
        uid: item.url,
        thumbUrl: item.url,
        status: 'done',
      });
    });
    return returnData;
  }, [sku?.artworks]);

  const checkFile = useMemoizedFn((formValue) => {
    setShowError(false);
    if (
      [
        ArtworkOptionEnum.FIXED_JOB_ARTWORK,
        ArtworkOptionEnum.ARTWORK_TEMPLATE,
      ].includes(formValue.artworkOption) &&
      !artworksList?.length
    ) {
      setShowError(true);
      return false;
    }
    return true;
  });

  const saveFileHandler = useMemoizedFn(async (fileList: any) => {
    const formValue = await form.validateFields();
    const fileItem = await dispatch.sku.skuAddFiles({
      id: sku.id,
      version: sku.version,
      artworks: [
        ...artworksList,
        { url: fileList.path, fileName: fileList.fileName },
      ],
      ...formValue,
    });
    setShowError(false);
    return fileItem;
    // return fileItem;
  });

  const changeFileListSort = async (fileList: Array<UploadFile<FileItem>>) => {
    const artworks = fileList.map((item) => ({
      url: item.url,
      fileName: item.name,
    }));
    const formValue = await form.validateFields();
    await dispatch.sku.skuAddFiles({
      id: sku.id,
      version: sku.version,
      artworks,
      ...formValue,
    });
  };

  const deleteFileHandler = useMemoizedFn(
    async (payload: FileItem | UploadFile<FileItem>) => {
      const formValue = await form.validateFields();
      const fileItem = await dispatch.sku.skuAddFiles({
        id: sku.id,
        version: sku.version,
        artworks: artworksList.filter((item) => item.url !== payload.url),
        ...formValue,
      });
      checkFile(formValue);
      return fileItem;
    }
  );

  const { run: debounceSubmit, cancel: cancelDebounceSubmit } = useDebounceFn(
    () => {
      submit();
    },
    { wait: 1000 }
  );

  const submit = useMemoizedFn(async () => {
    try {
      if (sku.readonly) {
        return true;
      }
      const formValue = await form.validateFields();
      const res = checkFile(formValue);
      if (!res) {
        return false;
      }
      await dispatch.sku.skuAddFiles({
        id: sku.id,
        version: sku.version,
        artworks: artworksList,
        ...formValue,
        // proofNeeded: [
        //   ArtworkOptionEnum.ARTWORK_TEMPLATE,
        //   ArtworkOptionEnum.DESIGN_ON_DEMAND,
        //   ArtworkOptionEnum.NONE,
        // ].includes(formValue.artworkOption)
        //   ? false
        //   : formValue.proofNeeded,
        proofNeeded: formValue.proofNeeded,
        // skipArtFile: [
        //   ArtworkOptionEnum.ARTWORK_TEMPLATE,
        //   ArtworkOptionEnum.DESIGN_ON_DEMAND,
        //   ArtworkOptionEnum.NONE,
        // ].includes(formValue.artworkOption)
        //   ? false
        //   : formValue.skipArtFile,
        skipArtFile:  formValue.skipArtFile,
      });

      return true;
    } catch {
      return false;
    }
  });

  const onValueChangeHandle = async () => {
    cancelDebounceSubmit();
    debounceSubmit();
  };

  return (
    <>
      <div className="single-sku-config-wizard-step-title">
        {t('platform.sku.wizard.uploadFiles')}
      </div>
      <div className="single-sku-config-wizard-step-content">
        <SectionHeading>
          {t('platform.sku.wizard.upload.title')}.
        </SectionHeading>
        <Form form={form} onValuesChange={onValueChangeHandle}>
          <Form.Item
            label={t('platform.sku.wizard.upload.fileDisplay')}
            name="artworkOption"
          >
            <Radio.Group disabled={sku.readonly || Boolean(topSku)}>
              <Radio value={ArtworkOptionEnum.NONE}>
                <Text>{t('platform.sku.wizard.upload.none')}</Text>
              </Radio>
              <Radio value={ArtworkOptionEnum.FIXED_JOB_ARTWORK}>
                <Text>{t('platform.sku.wizard.upload.fixedJobArtwork')}</Text>
              </Radio>
              <Radio value={ArtworkOptionEnum.ARTWORK_TEMPLATE}>
                <Text>{t('platform.sku.wizard.upload.artworkTemplate')}</Text>
              </Radio>
              <Radio value={ArtworkOptionEnum.DESIGN_ON_DEMAND} disabled>
                <Text>{t('platform.sku.wizard.upload.designOnDemand')}</Text>
              </Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            label={t('platform.sku.wizard.quantity.form.label.uploadArtFiles')}
            name="skipArtFile"
            // hidden={[
            //   ArtworkOptionEnum.ARTWORK_TEMPLATE,
            //   ArtworkOptionEnum.DESIGN_ON_DEMAND,
            //   ArtworkOptionEnum.NONE,
            // ].includes(form.getFieldValue('artworkOption'))}
            className="uploadFile-proof-needed"
          >
            <Radio.Group disabled={sku.readonly || Boolean(topSku)}>
              <Radio value={true}>
                <span>{t('common.yes')}</span>
              </Radio>
              <Radio value={false}>
                <span>{t('common.no')}</span>
              </Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            label={t('platform.sku.wizard.quantity.form.label.proofNeeded')}
            name="proofNeeded"
            // hidden={[
            //   ArtworkOptionEnum.ARTWORK_TEMPLATE,
            //   ArtworkOptionEnum.DESIGN_ON_DEMAND,
            //   ArtworkOptionEnum.NONE,
            // ].includes(form.getFieldValue('artworkOption'))}
            className="uploadFile-proof-needed"
          >
            <Radio.Group disabled={sku.readonly || Boolean(topSku)}>
              <Radio value={true}>
                <span>{t('common.yes')}</span>
              </Radio>
              <Radio value={false}>
                <span>{t('common.no')}</span>
              </Radio>
            </Radio.Group>
          </Form.Item>
        </Form>

        <div
          hidden={
            form.getFieldValue('artworkOption') === ArtworkOptionEnum.NONE
          }
        >
          <p>
            Put art files for this SKU here. Drag and drop them into the display
            order you’d like, with the first image being the SKU thumbnail.
          </p>
          <SkuArtFile
            showUploadButton={!(sku.readonly || Boolean(topSku))}
            displayNameForUrl={true}
            defaultFileList={artworksList}
            saveFileHandler={saveFileHandler}
            deleteFileHandler={deleteFileHandler}
            onFileListChange={changeFileListSort}
          />

          {showError && (
            <div className="ant-form-item-explain-error">
              {t('platform.sku.artFileRequired')}
            </div>
          )}
        </div>
      </div>
    </>
  );
};
